// src/pages/Stock/StockLocationsPage.jsx
// =============================================================================
// Stock Location maintenance page
// =============================================================================
//
// A stock location in hb_visits is deliberately broader than an ERP warehouse.
// It can be a main storage room, reception shelf, treatment room cabinet or any
// other logical place where the salon wants inventory visibility.
//
// Important business rule:
// - locations should normally be deactivated, not deleted,
// - existing stock movements and balances must remain historically readable,
// - moving stock between locations must always be done with a transfer movement,
//   not by editing a location row.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createStockLocation,
  listStockLocations,
  setStockLocationActive,
  updateStockLocation,
} from "../../services/stockMasterDataService";

import "./StockMasterDataPage.css";

const EMPTY_FORM = {
  code: "",
  name: "",
  type: "room",
  is_active: true,
  sort_order: 0,
};

const LOCATION_TYPES = ["main", "reception", "room", "employee", "virtual"];

function normalizeForm(row = {}) {
  return {
    code: row.code || "",
    name: row.name || "",
    type: row.type || "room",
    is_active: row.is_active !== false,
    sort_order: row.sort_order ?? 0,
  };
}

export default function StockLocationsPage() {
  const { t } = useTranslation(["stock", "common"]);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const isEditing = Boolean(editingId);

  async function loadRows() {
    setLoading(true);
    setError(null);
    try {
      const data = await listStockLocations({ includeInactive: includeInactive ? "true" : "false" });
      setRows(data);
    } catch (err) {
      setError(t("stock:masterData.locations.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    
  }, [includeInactive]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [row.code, row.name, row.type].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMessage(null);
    setError(null);
  }

  function editRow(row) {
    setForm(normalizeForm(row));
    setEditingId(row.id);
    setMessage(null);
    setError(null);
  }

  async function submitForm(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      if (isEditing) {
        await updateStockLocation(editingId, form);
        setMessage(t("stock:masterData.locations.messages.updated"));
      } else {
        await createStockLocation(form);
        setMessage(t("stock:masterData.locations.messages.created"));
      }
      resetForm();
      await loadRows();
    } catch (err) {
      setError(err?.message || t("stock:masterData.locations.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await setStockLocationActive(row.id, !row.is_active);
      setMessage(t("stock:masterData.locations.messages.statusUpdated"));
      await loadRows();
    } catch (err) {
      setError(err?.message || t("stock:masterData.locations.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="stock-md-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("stock:masterData.eyebrow")}</p>
          <h1 className="stock-md-title">{t("stock:masterData.locations.title")}</h1>
          <p className="stock-md-subtitle">{t("stock:masterData.locations.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={loadRows} disabled={loading}>
          {t("common:refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="stock-md-info-box">
        {t("stock:masterData.locations.guidance")}
      </section>

      {error && <div className="stock-md-error">{error}</div>}
      {message && <div className="stock-md-success">{message}</div>}

      <div className="stock-md-grid">
        <section className="stock-md-card">
          <h2 className="stock-md-card-title">
            {isEditing ? t("stock:masterData.locations.form.editTitle") : t("stock:masterData.locations.form.createTitle")}
          </h2>
          <p className="stock-md-card-description">{t("stock:masterData.locations.form.description")}</p>

          <form onSubmit={submitForm}>
            <div className="stock-md-form-grid">
              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.locations.fields.code")}</span>
                <input
                  className="stock-md-input"
                  value={form.code}
                  onChange={(e) => updateField("code", e.target.value.toUpperCase())}
                  disabled={saving}
                  required
                />
                <span className="stock-md-hint">{t("stock:masterData.locations.hints.code")}</span>
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.locations.fields.type")}</span>
                <select
                  className="stock-md-select"
                  value={form.type}
                  onChange={(e) => updateField("type", e.target.value)}
                  disabled={saving}
                >
                  {LOCATION_TYPES.map((type) => (
                    <option key={type} value={type}>{t(`stock:masterData.locations.types.${type}`)}</option>
                  ))}
                </select>
              </label>

              <label className="stock-md-form-field stock-md-form-field--full">
                <span className="stock-md-label">{t("stock:masterData.locations.fields.name")}</span>
                <input
                  className="stock-md-input"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  disabled={saving}
                  required
                />
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.common.sortOrder")}</span>
                <input
                  className="stock-md-input"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => updateField("sort_order", e.target.value)}
                  disabled={saving}
                />
              </label>

              <label className="stock-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                  disabled={saving}
                />
                {t("stock:masterData.common.active")}
              </label>
            </div>

            <div className="stock-md-actions">
              <button type="button" className="stock-md-button" onClick={resetForm} disabled={saving}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="submit" className="stock-md-button stock-md-button--primary" disabled={saving}>
                {isEditing ? t("stock:masterData.common.saveChanges") : t("stock:masterData.common.create")}
              </button>
            </div>
          </form>
        </section>

        <section className="stock-md-card">
          <div className="stock-md-toolbar">
            <div>
              <h2 className="stock-md-card-title">{t("stock:masterData.locations.listTitle")}</h2>
              <p className="stock-md-card-description">{t("stock:masterData.locations.listDescription")}</p>
            </div>
            <label className="stock-md-checkbox-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("stock:masterData.common.showInactive")}
            </label>
          </div>

          <input
            className="stock-md-input stock-md-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("stock:masterData.locations.searchPlaceholder")}
          />

          <div className="stock-md-table-wrap" style={{ marginTop: 12 }}>
            <table className="stock-md-table">
              <thead>
                <tr>
                  <th>{t("stock:masterData.locations.fields.code")}</th>
                  <th>{t("stock:masterData.locations.fields.name")}</th>
                  <th>{t("stock:masterData.locations.fields.type")}</th>
                  <th>{t("stock:masterData.common.status")}</th>
                  <th>{t("stock:masterData.common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td><div className="stock-md-row-title">{row.code}</div></td>
                    <td>{row.name}</td>
                    <td>{t(`stock:masterData.locations.types.${row.type || "room"}`)}</td>
                    <td>
                      <span className={`stock-md-badge ${row.is_active ? "" : "stock-md-badge--inactive"}`}>
                        {row.is_active ? t("stock:masterData.common.active") : t("stock:masterData.common.inactive")}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="stock-md-button" onClick={() => editRow(row)}>
                        {t("common:edit", { defaultValue: "Edit" })}
                      </button>{" "}
                      <button
                        type="button"
                        className="stock-md-button stock-md-button--dangerish"
                        onClick={() => toggleActive(row)}
                        disabled={saving}
                      >
                        {row.is_active ? t("stock:masterData.common.deactivate") : t("stock:masterData.common.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr>
                    <td colSpan="5">{loading ? t("common:loading", { defaultValue: "Loading…" }) : t("stock:masterData.locations.empty")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
